<template>
  <div class="change-brief-status">
    <div class="change-brief-status__title">{{title}}</div>
    <div class="change-brief-status__subtitle">{{subtitle}}:</div>
    <div class="change-brief-status__header">
      <base-select
        class="change-brief-status__select"
        border-less="true"
        :options="options"
        v-model="statusId"
        alignLeft="true"
        :max-height-list="450"
        :titleStyle="titleStyle"
        :headerStyle="selectStyle"
        @change="changeStatus"
        placeholder="Select Status"
        :placeholderStyle="{'padding': '0'}"
      />
    </div>
    <div class="change-brief-status__controls">
      <button class="change-brief-status__button change-brief-status__button_cancel"
              @click.prevent="cancel()">Cancel
      </button>
      <button class="change-brief-status__button change-brief-status__button_submit"
              @click.prevent="apply()">
        Apply
      </button>
    </div>
  </div>
</template>

<script>
import Filters from '@/common/Filters'
import { mapState } from 'vuex'
import { ALLOWED_CONTENT_BRIEF_STATUS_NAMES_FOR_ADMIN, ALLOWED_CONTENT_BRIEF_STATUS_NAMES_FOR_CLIENT_COLLABORATOR } from '@/common/AllowedUrlStatuses'
import helper from '@/services/Helper'

export const STATUS_TYPES = {
  BRIEF_STATUS: 'BRIEF_STATUS',
  URL_STATUS: 'URL_STATUS',
  OPTIMIZED_STATUS: 'OPTIMIZED_STATUS'
}

const SELECT_STYLE = {
  'background-color': 'transparent',
  color: '#284aff',
  'text-decoration': 'underline',
  width: '125px',
  padding: '0'
}
const ERROR_SELECT_STYLE = {
  'background-color': 'transparent',
  color: '#e11c21',
  'text-decoration': 'underline',
  width: '125px',
  padding: '0'
}
/**
 * Modal for changing url and content brief statuses of current content brief
 */
export default {
  name: 'ChangeStatusPopup',
  // type of changing status
  props: ['type', 'brief'],
  data () {
    return {
      isValidating: false,
      types: STATUS_TYPES,
      descriptionChanged: false,
      titleStyle: {
        overflow: 'visible'
      },
      statusId: null,
      description: null
    }
  },
  watch: {
    brief: {
      handler () {
        this.statusId = this.getStatusId()
      },
      immediate: true
    }
  },
  computed: {
    ...mapState({
      urlStatuses (state) {
        const statuses = state.urlStatuses.map(helper.convertListToOptions) || []
        const index = statuses.findIndex(item => item.value === this.brief.url_status_id)
        if (!~index) {
          statuses.push({
            label: this.brief.url_status,
            value: this.brief.url_status_id
          })
        }
        return statuses
      },
      contentBriefStatuses (state) {
        let allowedStatuses = []
        if (this.isCurrentUserClientCollaborator) {
          allowedStatuses = ALLOWED_CONTENT_BRIEF_STATUS_NAMES_FOR_CLIENT_COLLABORATOR
        }
        let statuses = state.contentBriefStatuses.filter(item => !allowedStatuses.length || allowedStatuses.includes(item.label.toLowerCase()))
          .map(helper.convertListToOptions) || []
        // if (this.brief.content_brief_status.toLowerCase() === 'draft') {
        //   statuses = statuses.filter(item => item.label.toLowerCase() !== 'revision')
        // }
        // add current CB status if it's not present in the ALLOWED_CONTENT_BRIEF_STATUS_NAMES_FOR_*
        const index = statuses.findIndex(item => item.value === this.brief.content_brief_status_id)
        if (!~index) {
          statuses.push({
            label: this.brief.content_brief_status,
            value: this.brief.content_brief_status_id
          })
        }
        return statuses
      }
    }),
    selectStyle () {
      return this.isSelectError ? ERROR_SELECT_STYLE : SELECT_STYLE
    },
    title () {
      switch (this.type) {
        case STATUS_TYPES.BRIEF_STATUS:
          return 'Change Brief Status'
        case STATUS_TYPES.URL_STATUS:
          return 'Change URL Status'
        case STATUS_TYPES.OPTIMIZED_STATUS:
          return 'Optimized'
      }
    },
    optimizedStatuses () {
      return [
        {
          label: 'Yes',
          value: 1
        },
        {
          label: 'No',
          value: 0
        }
      ]
    },
    subtitle () {
      switch (this.type) {
        case STATUS_TYPES.BRIEF_STATUS:
          return 'Brief Status'
        case STATUS_TYPES.URL_STATUS:
          return 'URL Status'
        case STATUS_TYPES.OPTIMIZED_STATUS:
          return 'Optimization Status'
      }
    },
    options () {
      switch (this.type) {
        case STATUS_TYPES.BRIEF_STATUS:
          return this.contentBriefStatuses
        case STATUS_TYPES.URL_STATUS:
          return this.urlStatuses
        case STATUS_TYPES.OPTIMIZED_STATUS:
          return this.optimizedStatuses
      }
    },
    isDescriptionError () {
      if (this.type === STATUS_TYPES.URL_STATUS) {
        return false
      }
      return this.isValidating && !this.description
    },
    isSelectError () {
      if (this.type === STATUS_TYPES.OPTIMIZED_STATUS) {
        return false
      }
      return this.isValidating && (!this.statusId || this.getStatusId() === this.statusId)
    }

  },
  methods: {
    cancel () {
      this.$emit('cancel')
    },
    isFormInvalid () {
      return this.isSelectError || this.isDescriptionError
    },
    changeStatus (statusId) {
      if (!this.descriptionChanged) {
        if (this.getStatusId() === this.statusId) {
          this.description = ''
        } else {
          const status = this.options.find(item => item.value === statusId)
          // generated description
          this.description = `${this.subtitle} was changed to ${status.label}.`
        }
      }
    },
    apply () {
      this.isValidating = true
      if (this.isFormInvalid()) {
        return
      }
      this.$emit('apply', { type: this.type, statusId: this.statusId, description: this.description })
    },
    calculateElementWidth (el) {
      return el.offsetWidth
    },
    getStatusId () {
      switch (this.type) {
        case STATUS_TYPES.BRIEF_STATUS:
          return this.brief.content_brief_status_id
        case STATUS_TYPES.URL_STATUS:
          return this.brief.url_status_id
        case STATUS_TYPES.OPTIMIZED_STATUS:
          return this.brief.is_optimized
      }
    }
  },
  filters: {
    ...Filters
  }
}
</script>

<style lang="scss">
  .change-brief-status {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 40px;

    &__select {
      color: $rfx-medium-march;
      margin-left: 23px;
    }

    &__title {
      text-align: center;
      font-size: 34px;
      font-weight: 500;
      margin: 9px 0 35px;
      color: $rfx-dark-march;
    }

    &__header {
      background-color: rgba(1, 22, 39, 0.1);
      height: 55px;
      display: flex;
      padding: 0 40px;
      align-items: center;
      margin: 0 -40px;
    }

    &__subtitle {
      color: $rfx-dark-march;
      font-weight: bold;
      margin-bottom: 10px;

    }

    &__info-item {
      color: $rfx-light-march;
      border: none;
      padding: 5px 10px 22px;

    }

    &__input {
      display: flex;
      flex-direction: column;
      position: relative;
      margin-top: 30px;

      &_invalid[type='text'] {
        border: 1px solid $rfx-red;
        border-radius: 4px;
      }
    }

    &__label {
      display: block;
      font-weight: 600;
      margin-bottom: 5px;
      color: $rfx-dark-march;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;

        &:disabled {
          pointer-events: none;
          background-color: #fff;
          color: $rfx-neutral-grey;
          cursor: default;
        }
      }

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
      }
    }

    &__error-message {
      color: $rfx-red;
      font-size: 10px;
      margin-top: 5px;
    }

  }

  .form-control {
    height: 50px;
    padding: 0 20px;
    border: 2px solid $rfx-light-steel;
    outline: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    color: $rfx-dark-march;

    &::placeholder {
      color: $rfx-pearl;
      font-weight: normal;
      font-size: 14px;
    }

    &:disabled {
      background-color: $rfx-light-steel;
    }

    &_error {
      border-color: $rfx-red;
      color: $rfx-red;

      &::placeholder {
        color: $rfx-red;
        font-size: 13px;
      }

    }
  }

  .description {
    width: 100%;
    padding: 0;
    resize: none;
    outline: none;
    border: none;
    font-size: 14px;

    &_invalid {
      border: 1px solid $rfx-red;
      border-radius: 4px;
    }

    &::placeholder {
      color: $rfx-pearl;
      font-weight: normal;
      font-size: 14px;
      margin-top: -5px;
    }

    &__wrapper {
      padding: 10px 20px;
      height: auto;
    }
  }

  .error {
    &__message-description,
    &__message {
      color: $rfx-red;
      position: absolute;
      top: 75px;
      font-size: 10px;
      font-weight: normal;
    }

    &__message-description {
      top: 154px;
      left: 0;
    }
  }

</style>
